import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, FlaskConical, Trophy, AlertCircle, CheckCircle, Calendar, Send, X } from 'lucide-react';
import { useSelection } from '../../context/SelectionContext';
import { useToast } from '../common/Toast';
import api from '../../utils/api';

export default function ResearchCourseCard({ course, initialSubmitted, onSubmissionChange, groupedCourses, displayName }) {
  // If this is a grouped card, we have multiple courses sharing the same display name
  const isGrouped = groupedCourses && groupedCourses.length > 1;
  const cardTitle = displayName || course.title;
  const { t } = useTranslation('research');
  const { selectedChild, triggerIncompleteCountRefetch } = useSelection();
  const toast = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Ticket modal state
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [ticketMessage, setTicketMessage] = useState('');
  const [isSubmittingTicket, setIsSubmittingTicket] = useState(false);
  const [ticketStatus, setTicketStatus] = useState(null);

  // Default dates - fallback when no date config is provided
  const getDefaultDates = () => [
    { id: Date.now(), label: 'Application Deadline', date: '', notify: true },
    { id: Date.now() + 1, label: 'Test Deadline', date: '', notify: true },
  ];

  // Build dates from config
  const buildDatesFromConfig = (config, savedDates = []) => {
    if (!config || config.length === 0) {
      return getDefaultDates();
    }
    const savedDatesMap = {};
    savedDates.forEach(dt => {
      savedDatesMap[dt.label] = dt.date || '';
    });
    return config.map((cfg, idx) => ({
      id: Date.now() + idx,
      key: cfg.key,
      label: cfg.label,
      date: savedDatesMap[cfg.label] || '',
      notify: true,
      optional: cfg.optional || false,
    }));
  };

  // Form state
  const [dateConfig, setDateConfig] = useState(null); // Config from API
  const [dates, setDates] = useState(getDefaultDates());
  // const [removingIds, setRemovingIds] = useState(new Set()); // Track items being removed for exit animation - temporarily disabled
  const [formData, setFormData] = useState({
    eligibility: '',
    test_rules: '',
    additional_notes: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(initialSubmitted ?? false);
  const [validationError, setValidationError] = useState(null);
  const [shakeKey, setShakeKey] = useState(0);

  const isResearch = course.code?.startsWith('R-');
  const Icon = isResearch ? FlaskConical : Trophy;
  const accentColor = isResearch ? '#6366f1' : '#f59e0b';
  const needsInput = course.needsInput !== false; // Default to true if not specified

  const loadCompetitionDetails = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get(`/parent/children/${selectedChild.id}/courses/${course.id}/competition-details`);

      // Get date config from API response (new dynamic dates feature)
      const config = response.data.date_config;
      if (config && Array.isArray(config) && config.length > 0) {
        setDateConfig(config);
      } else {
        setDateConfig(null); // Will use default dates
      }

      if (response.data.details) {
        const d = response.data.details;
        // Build dates based on config or use defaults
        const savedDates = d.dates && Array.isArray(d.dates) ? d.dates : [];
        setDates(buildDatesFromConfig(config, savedDates));

        setFormData({
          eligibility: d.eligibility || '',
          test_rules: d.test_rules || '',
          additional_notes: d.additional_notes || '',
        });
        setIsSubmitted(d.is_submitted || false);
      } else {
        // No saved details yet, initialize with config or defaults
        setDates(buildDatesFromConfig(config, []));
      }
    } catch (err) {
      console.error('Failed to load competition details:', err);
    } finally {
      setIsLoading(false);
    }
  }, [selectedChild?.id, course.id]);


  // Fetch full details when card is opened
  useEffect(() => {
    if (isOpen && selectedChild?.id && course.id) {
      loadCompetitionDetails();
    }
  }, [isOpen, selectedChild?.id, course.id, loadCompetitionDetails]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Date management functions - temporarily disabled
  /*
  const addDate = () => {
    const newId = Date.now();
    setDates(prev => [...prev, { id: newId, label: '', date: '', notify: false, isNew: true }]);
    // Remove isNew flag after animation completes
    setTimeout(() => {
      setDates(prev => prev.map(d => d.id === newId ? { ...d, isNew: false } : d));
    }, 300);
  };

  const removeDate = (id) => {
    // Start exit animation
    setRemovingIds(prev => new Set([...prev, id]));
    // Actually remove after animation completes
    setTimeout(() => {
      setDates(prev => prev.filter(d => d.id !== id));
      setRemovingIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }, 250);
  };
  */

  const updateDate = (id, field, value) => {
    setDates(prev => prev.map(d => d.id === id ? { ...d, [field]: value } : d));
    // Clear validation error when user makes changes
    if (validationError) setValidationError(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedChild?.id || !course.id) return;

    // When date config is present (dynamic dates), required dates must be filled
    // Otherwise (backwards compatibility), at least one date is required
    const filledDates = dates.filter(d => d.date);
    const missingRequiredDates = dates.filter(d => !d.date && !d.optional);

    if (dateConfig && dateConfig.length > 0) {
      // Dynamic dates mode: only non-optional dates are required
      if (missingRequiredDates.length > 0) {
        setValidationError(t('validation.allDatesRequired'));
        setShakeKey(k => k + 1);
        return;
      }
    } else {
      // Legacy mode: at least one date required
      if (filledDates.length === 0) {
        setValidationError(t('validation.enterAtLeastOneDate'));
        setShakeKey(k => k + 1);
        return;
      }
    }

    setValidationError(null);
    setShowConfirmModal(true);
  };

  const handleConfirmedSubmit = async () => {
    setShowConfirmModal(false);
    setIsSubmitting(true);
    setSaveMessage(null);

    try {
      const payload = {
        ...formData,
        dates: dates.filter(d => d.date), // Only send dates that have values
      };

      // If grouped, save to all courses in the group
      const coursesToSave = isGrouped ? groupedCourses : [course];

      for (const c of coursesToSave) {
        await api.post(`/parent/children/${selectedChild.id}/courses/${c.id}/competition-details`, payload);
      }

      setSaveMessage({ type: 'success', text: t('messages.savedSuccess') });
      setIsSubmitted(true);

      // Refresh data from server to ensure state is synchronized
      await loadCompetitionDetails();

      onSubmissionChange?.(); // Notify parent to refresh incomplete count
      triggerIncompleteCountRefetch(); // Notify header badge to refresh
      toast.success(t('toast.detailsSavedSuccess', { course: cardTitle }), {
        title: t('toast.detailsSubmitted'),
        duration: 5000,
      });
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (err) {
      setSaveMessage({ type: 'error', text: t('messages.saveFailed') });
      toast.error(t('toast.detailsSaveFailed'));
      console.error('Failed to save:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitTicket = async () => {
    if (!ticketMessage.trim() || !selectedChild?.id || !course.id) return;

    setIsSubmittingTicket(true);
    setTicketStatus(null);

    try {
      await api.post(`/parent/children/${selectedChild.id}/courses/${course.id}/support-ticket`, {
        message: ticketMessage,
        courseName: isGrouped ? cardTitle : `${course.code} - ${course.title}`,
      });
      setTicketStatus({ type: 'success', text: t('ticket.success') });
      setTicketMessage('');
      toast.success(t('toast.requestSentMessage'), {
        title: t('toast.requestSent'),
        duration: 5000,
      });
      setTimeout(() => {
        setShowTicketModal(false);
        setTicketStatus(null);
      }, 2000);
    } catch (err) {
      setTicketStatus({ type: 'error', text: t('ticket.failed') });
      toast.error(t('toast.requestFailed'));
      console.error('Failed to submit ticket:', err);
    } finally {
      setIsSubmittingTicket(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl mb-3 sm:mb-4 shadow-sm hover:shadow-md dark:shadow-gray-900/30 transition-all duration-200 overflow-hidden card-interactive">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full text-left p-3 sm:p-5"
      >
        <div className="flex items-center justify-between gap-2 sm:gap-4">
          <div className="flex items-center gap-2.5 sm:gap-4 min-w-0 flex-1">
            <div
              className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center"
              style={{ backgroundColor: `${accentColor}15`, color: accentColor }}
            >
              <Icon className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={1.5} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                <h3 className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base truncate">
                  {cardTitle}
                </h3>
                {!needsInput ? (
                  <span className="flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                    <CheckCircle className="w-3 h-3" />
                    {t('badge.fixedSchedule')}
                  </span>
                ) : isSubmitted ? (
                  <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
                ) : (
                  <span className="px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
                    {t('badge.needsInput')}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className={`flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center transition-colors ${
            isOpen ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-50 dark:hover:bg-gray-700'
          }`}>
            <ChevronDown
              className={`w-4 h-4 sm:w-5 sm:h-5 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            />
          </div>
        </div>
      </button>

      {/* Expanded content with smooth animation */}
      <div className="accordion-wrapper" data-open={isOpen}>
        <div className="accordion-content">
          <div className="bg-gray-50 dark:bg-gray-900 px-3 py-3 sm:px-5 sm:py-4 border-t border-gray-100 dark:border-gray-700">
            {!needsInput ? (
              /* Fixed schedule - no input needed */
              <div className="bg-white dark:bg-gray-800 rounded-lg p-3 sm:p-4 border border-gray-200 dark:border-gray-700">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-[#589DD5] shrink-0 mt-0.5" />
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                    {t('notice.fixedSchedule')}
                  </p>
                </div>
              </div>
            ) : isLoading ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">{t('messages.loading')}</div>
            ) : isSubmitted ? (
              /* Read-only view for submitted courses */
              <div className="space-y-4">
                {/* Important Dates - Read Only */}
                {dates.filter(d => d.label || d.date).length > 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-3 sm:p-4 border border-gray-200 dark:border-gray-700">
                    <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-3 flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {t('dates.title')}
                    </h4>
                    <div className="space-y-2">
                      {dates.filter(d => d.label || d.date).map((dateItem, idx) => (
                        <div key={idx} className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
                          <span className="text-sm text-gray-600 dark:text-gray-400">{dateItem.label === 'Application Deadline' ? t('dates.applicationDeadline') : dateItem.label === 'Test Deadline' ? t('dates.testDeadline') : dateItem.label || t('dates.unlabeled')}</span>
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {dateItem.date || '—'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Details - Read Only */}
                {(formData.eligibility || formData.test_rules || formData.additional_notes) && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-3 sm:p-4 border border-gray-200 dark:border-gray-700 space-y-3">
                    {formData.eligibility && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">{t('details.eligibility')}</p>
                        <p className="text-sm text-gray-700 dark:text-gray-300">{formData.eligibility}</p>
                      </div>
                    )}
                    {formData.test_rules && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">{t('details.rules')}</p>
                        <p className="text-sm text-gray-700 dark:text-gray-300">{formData.test_rules}</p>
                      </div>
                    )}
                    {formData.additional_notes && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">{t('details.additionalNotes')}</p>
                        <p className="text-sm text-gray-700 dark:text-gray-300">{formData.additional_notes}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Empty state if nothing was entered */}
                {dates.filter(d => d.label || d.date).length === 0 && !formData.eligibility && !formData.test_rules && !formData.additional_notes && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-3 sm:p-4 border border-gray-200 dark:border-gray-700 text-center">
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t('details.noInfoProvided')}</p>
                  </div>
                )}

                {/* Submit Ticket Button - temporarily disabled */}
                {/* <div className="flex justify-end pt-2">
                  <button
                    type="button"
                    onClick={() => setShowTicketModal(true)}
                    className="px-4 py-2 text-sm font-medium text-white bg-[#589DD5] rounded-lg hover:bg-[#4a8bc4] transition-colors"
                  >
                    Request Changes
                  </button>
                </div> */}
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Important Notice */}
              <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 sm:p-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-[#589DD5] flex-shrink-0 mt-0.5" />
                  <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                    <p className="font-semibold mb-1 text-gray-700 dark:text-gray-300">{t('notice.title')}</p>
                    <p>{t('notice.description')}</p>
                  </div>
                </div>
              </div>

              {/* Important Dates */}
              <div className="bg-white dark:bg-gray-800 rounded-lg p-3 sm:p-4 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {t('dates.title')}
                  </h4>
                  <span className="text-[10px] text-gray-500 dark:text-gray-400">
                    <span className="text-red-500">*</span> {dateConfig && dateConfig.length > 0 ? t('dates.allRequired') : t('dates.atLeastOneRequired')}
                  </span>
                </div>
                <div className="space-y-3">
                  {dates.map((dateItem) => (
                    <div
                      key={dateItem.id}
                      className={`flex flex-col sm:flex-row sm:items-center gap-2 p-2 sm:p-0 bg-gray-50 dark:bg-gray-900 sm:bg-transparent sm:dark:bg-transparent rounded-lg sm:rounded-none ${
                        dateItem.isNew ? 'animate-fade-in-up' : ''
                      }`}
                    >
                      {/* Top row on mobile: Label (read-only) */}
                      <div className="flex items-center gap-2 w-full sm:w-auto">
                        {/* Label - fixed, not editable */}
                        <span className="flex-1 sm:flex-none sm:w-40 px-2 py-2 text-xs font-medium text-gray-700 dark:text-gray-300">
                          {dateItem.label === 'Application Deadline' ? t('dates.applicationDeadline') : dateItem.label === 'Test Deadline' ? t('dates.testDeadline') : dateItem.label}
                          {dateItem.optional && <span className="text-gray-400 dark:text-gray-500 font-normal ml-1">{t('details.optional').toLowerCase()}</span>}
                        </span>
                      </div>

                      {/* Bottom row on mobile: Date */}
                      <div className="flex items-center gap-2 w-full sm:flex-1">
                        {/* Date input */}
                        <input
                          type="date"
                          value={dateItem.date}
                          onChange={(e) => updateDate(dateItem.id, 'date', e.target.value)}
                          className="flex-1 px-3 py-2 text-sm border-2 border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:border-[#589DD5] focus:ring-4 focus:ring-[#589DD5]/20 bg-white dark:bg-gray-700 dark:text-white transition-all [&::-webkit-calendar-picker-indicator]:cursor-pointer dark:[&::-webkit-calendar-picker-indicator]:invert"
                        />
                        {/* Clear button - only show when date has value */}
                        {dateItem.date && (
                          <button
                            type="button"
                            onClick={() => updateDate(dateItem.id, 'date', '')}
                            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors"
                            title={t('dates.clear')}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  {/* Add date button - temporarily disabled
                  <button
                    type="button"
                    onClick={addDate}
                    className="w-full sm:w-auto flex items-center justify-center sm:justify-start gap-1.5 px-3 py-2 sm:py-1.5 text-xs font-medium text-[#589DD5] hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors border border-dashed border-[#589DD5]/30 sm:border-transparent"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Add Date
                  </button>
                  */}
                </div>
              </div>

              {/* Details - Optional */}
              <div className="bg-white dark:bg-gray-800 rounded-lg p-3 sm:p-4 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 mb-3">
                  <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    {t('details.title')}
                  </h4>
                  <span className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase">{t('details.optional')}</span>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      {t('details.eligibility')} <span className="text-gray-400 dark:text-gray-500 font-normal">{t('details.optional').toLowerCase()}</span>
                    </label>
                    <textarea
                      value={formData.eligibility}
                      onChange={(e) => handleInputChange('eligibility', e.target.value)}
                      placeholder={t('details.eligibilityPlaceholder')}
                      rows={2}
                      className="w-full px-3 py-2 text-sm border-2 border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:border-[#589DD5] focus:ring-4 focus:ring-[#589DD5]/20 resize-none bg-white dark:bg-gray-700 dark:text-white dark:placeholder-gray-500 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      {t('details.rules')} <span className="text-gray-400 dark:text-gray-500 font-normal">{t('details.optional').toLowerCase()}</span>
                    </label>
                    <textarea
                      value={formData.test_rules}
                      onChange={(e) => handleInputChange('test_rules', e.target.value)}
                      placeholder={t('details.rulesPlaceholder')}
                      rows={2}
                      className="w-full px-3 py-2 text-sm border-2 border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:border-[#589DD5] focus:ring-4 focus:ring-[#589DD5]/20 resize-none bg-white dark:bg-gray-700 dark:text-white dark:placeholder-gray-500 transition-all"
                    />
                  </div>
                </div>
              </div>

              {/* Additional Notes - Optional */}
              <div className="bg-white dark:bg-gray-800 rounded-lg p-3 sm:p-4 border border-gray-200 dark:border-gray-700">
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-2">
                  {t('details.additionalNotes')} <span className="text-[10px] font-medium text-gray-400 dark:text-gray-500 normal-case">{t('details.optional').toLowerCase()}</span>
                </label>
                <textarea
                  value={formData.additional_notes}
                  onChange={(e) => handleInputChange('additional_notes', e.target.value)}
                  placeholder={t('details.additionalNotesPlaceholder')}
                  rows={3}
                  className="w-full px-3 py-2 text-sm border-2 border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:border-[#589DD5] focus:ring-4 focus:ring-[#589DD5]/20 resize-none bg-white dark:bg-gray-700 dark:text-white dark:placeholder-gray-500 transition-all"
                />
              </div>

              {/* Validation Error */}
              {validationError && (
                <div
                  key={shakeKey}
                  className="flex items-center gap-2 text-sm px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 animate-error-shake"
                >
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  {validationError}
                </div>
              )}

              {/* Save Message */}
              {saveMessage && (
                <div className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg ${
                  saveMessage.type === 'success' ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                }`}>
                  {saveMessage.type === 'success' ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    <AlertCircle className="w-4 h-4" />
                  )}
                  {saveMessage.text}
                </div>
              )}

              {/* Submit Button */}
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full sm:w-auto px-5 py-2.5 sm:py-2 bg-[#589DD5] dark:bg-transparent dark:border dark:border-[#589DD5] text-white dark:text-[#589DD5] rounded-lg hover:bg-[#4a8bc4] dark:hover:bg-[#589DD5]/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                >
                  {isSubmitting ? t('actions.loading', { ns: 'common' }) : t('actions.submit', { ns: 'common' })}
                </button>
              </div>

                {/* Content Lock Warning */}
                {!isSubmitted && (
                  <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 text-center">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t('notice.contentLock')}
                    </p>
                  </div>
                )}
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Modal - rendered via portal to ensure full-screen overlay */}
      {showConfirmModal && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowConfirmModal(false)}
          />

          {/* Modal */}
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full mx-4 overflow-hidden animate-fade-in-up max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="bg-[#589DD5]/10 dark:bg-[#589DD5]/20 px-4 py-3 sm:px-6 sm:py-4 border-b border-[#589DD5]/20 shrink-0">
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                  {t('modal.confirmTitle')}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{course.code} - {course.title}</p>
              </div>
            </div>

            {/* Content */}
            <div className="px-4 py-4 sm:px-6 sm:py-5 overflow-y-auto">
              {/* Warning */}
              <div className="bg-[#589DD5]/10 dark:bg-[#589DD5]/20 rounded-lg p-3 border border-[#589DD5]/30 mb-4">
                <p className="text-xs sm:text-sm text-gray-700 dark:text-gray-300">
                  <span className="font-semibold">Important:</span> {t('modal.confirmWarning')}
                </p>
              </div>

              {/* Summary of entered information */}
              <div className="space-y-3">
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('modal.yourInfo')}
                </h4>

                {/* Dates */}
                {dates.filter(d => d.label || d.date).length > 0 && (
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5" />
                      {t('dates.title')}
                    </p>
                    <div className="space-y-1.5">
                      {dates.filter(d => d.label || d.date).map((d, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-300">{d.label === 'Application Deadline' ? t('dates.applicationDeadline') : d.label === 'Test Deadline' ? t('dates.testDeadline') : d.label || t('dates.unlabeled')}</span>
                          <span className="font-medium text-gray-900 dark:text-white">
                            {d.date || '—'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Details */}
                {(formData.eligibility || formData.test_rules || formData.additional_notes) && (
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700 space-y-2">
                    {formData.eligibility && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('details.eligibility')}</p>
                        <p className="text-sm text-gray-700 dark:text-gray-300 mt-0.5">{formData.eligibility}</p>
                      </div>
                    )}
                    {formData.test_rules && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('details.rules')}</p>
                        <p className="text-sm text-gray-700 dark:text-gray-300 mt-0.5">{formData.test_rules}</p>
                      </div>
                    )}
                    {formData.additional_notes && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('details.additionalNotes')}</p>
                        <p className="text-sm text-gray-700 dark:text-gray-300 mt-0.5">{formData.additional_notes}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Empty state if nothing entered */}
                {dates.filter(d => d.label || d.date).length === 0 && !formData.eligibility && !formData.test_rules && !formData.additional_notes && (
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700 text-center">
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t('modal.noInfoEntered')}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="px-4 py-3 sm:px-6 sm:py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end shrink-0">
              <button
                type="button"
                onClick={() => setShowConfirmModal(false)}
                className="w-full sm:w-auto px-4 py-2.5 sm:py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                {t('actions.cancel', { ns: 'common' })}
              </button>
              <button
                type="button"
                onClick={handleConfirmedSubmit}
                className="w-full sm:w-auto px-5 py-2.5 sm:py-2 text-sm font-medium text-white bg-[#589DD5] rounded-lg hover:bg-[#4a8bc4] transition-colors"
              >
                {t('actions.submit', { ns: 'common' })}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Ticket Modal - rendered via portal */}
      {showTicketModal && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => {
              if (!isSubmittingTicket) {
                setShowTicketModal(false);
                setTicketMessage('');
                setTicketStatus(null);
              }
            }}
          />

          {/* Modal */}
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full mx-4 overflow-hidden animate-fade-in-up">
            {/* Header */}
            <div className="bg-[#589DD5]/10 dark:bg-[#589DD5]/20 px-4 py-3 sm:px-6 sm:py-4 border-b border-[#589DD5]/20">
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                  {t('ticket.title')}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{course.code} - {course.title}</p>
              </div>
            </div>

            {/* Content */}
            <div className="px-4 py-4 sm:px-6 sm:py-5">
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                {t('ticket.description')}
              </p>

              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                  {t('ticket.label')}
                </label>
                <textarea
                  value={ticketMessage}
                  onChange={(e) => setTicketMessage(e.target.value)}
                  placeholder={t('ticket.placeholder')}
                  rows={5}
                  disabled={isSubmittingTicket}
                  className="w-full px-3 py-2 text-sm border-2 border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:border-[#589DD5] focus:ring-4 focus:ring-[#589DD5]/20 resize-none bg-white dark:bg-gray-700 dark:text-white dark:placeholder-gray-500 transition-all disabled:opacity-50"
                />
              </div>

              {/* Status message */}
              {ticketStatus && (
                <div className={`mt-3 flex items-center gap-2 text-sm px-3 py-2 rounded-lg ${
                  ticketStatus.type === 'success' ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                }`}>
                  {ticketStatus.type === 'success' ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    <AlertCircle className="w-4 h-4" />
                  )}
                  {ticketStatus.text}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="px-4 py-3 sm:px-6 sm:py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
              <button
                type="button"
                onClick={() => {
                  setShowTicketModal(false);
                  setTicketMessage('');
                  setTicketStatus(null);
                }}
                disabled={isSubmittingTicket}
                className="w-full sm:w-auto px-4 py-2.5 sm:py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
              >
                {t('actions.cancel', { ns: 'common' })}
              </button>
              <button
                type="button"
                onClick={handleSubmitTicket}
                disabled={isSubmittingTicket || !ticketMessage.trim()}
                className="w-full sm:w-auto px-5 py-2.5 sm:py-2 text-sm font-medium text-white bg-[#589DD5] rounded-lg hover:bg-[#4a8bc4] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <Send className="w-4 h-4" />
                {isSubmittingTicket ? t('ticket.sending') : t('ticket.sendRequest')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
